import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { useAuth } from '@/context/AuthContext';
import { 
  getTask, 
  updateTask, 
  deleteTask, 
  addTaskStep,
  updateTaskStep,
  deleteTaskStep,
  getAgents,
  getFunctionalAreas,
  getPriorities,
  getStatuses,
  uploadFile,
  toggleTaskVisibility 
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  ArrowLeft, 
  Loader2, 
  Edit2, 
  Trash2, 
  Plus,
  Calendar,
  User,
  Building,
  Clock,
  FileText,
  Link as LinkIcon,
  Image,
  Save,
  MoreVertical,
  Upload,
  X,
  Eye,
  EyeOff,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import DropZone from '@/components/DropZone';
import RichTextEditor from '@/components/RichTextEditor';

const priorityColors = {
  'Urgente': 'bg-red-100 text-red-700 border-red-200',
  'Alta': 'bg-orange-100 text-orange-700 border-orange-200',
  'Media-Alta': 'bg-amber-100 text-amber-700 border-amber-200',
  'Media': 'bg-blue-100 text-blue-700 border-blue-200',
  'Media-Baja': 'bg-slate-100 text-slate-700 border-slate-200',
  'Baja': 'bg-stone-100 text-stone-600 border-stone-200',
};

const statusColors = {
  'Pendiente': 'bg-amber-100 text-amber-700 border-amber-200',
  'En Proceso': 'bg-blue-100 text-blue-700 border-blue-200',
  'Completada': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Bloqueada': 'bg-red-100 text-red-700 border-red-200',
  'Cancelada': 'bg-slate-100 text-slate-500 border-slate-200',
};

const TaskDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canManageTasks, isSuperadmin } = useAuth();
  
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showStepDialog, setShowStepDialog] = useState(false);
  
  // Config
  const [areas, setAreas] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [agents, setAgents] = useState([]);
  
  // Edit form
  const [editForm, setEditForm] = useState({});
  
  // New step form
  const [newStep, setNewStep] = useState({ description: '', performed_by: '' });
  
  // Edit step
  const [editingStep, setEditingStep] = useState(null);
  const [editStepForm, setEditStepForm] = useState({ description: '', performed_by: '' });
  
  // Edit attachments
  const [editUploading, setEditUploading] = useState(false);
  const [editNewUrl, setEditNewUrl] = useState('');

  // Email modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [emailBody, setEmailBody] = useState('');

  const openEmailModal = () => {
    const allAgents = [
      ...(task.internal_agents_details || []),
      ...(task.external_agents_details || [])
    ].filter(a => a.email);
    setEmailRecipients(allAgents.map(a => ({ ...a, selected: true })));
    setEmailBody('');
    setShowEmailModal(true);
  };

  const sendEmail = () => {
    const to = emailRecipients.filter(r => r.selected).map(r => r.email).join(',');
    const subject = encodeURIComponent(`Tarea ${task.sequential_number} - ${task.title}`);
    const body = encodeURIComponent(emailBody);
    window.open(`mailto:${to}?subject=${subject}&body=${body}`, '_self');
    setShowEmailModal(false);
  };

  useEffect(() => {
    loadTask();
    loadConfig();
  }, [id]);

  const loadTask = async () => {
    try {
      const response = await getTask(id);
      setTask(response.data);
      setEditForm(response.data);
    } catch (error) {
      console.error('Error loading task:', error);
      toast.error('Error al cargar la tarea');
      navigate('/tasks');
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    try {
      const [areasRes, prioritiesRes, statusesRes, agentsRes] = await Promise.all([
        getFunctionalAreas(),
        getPriorities(),
        getStatuses(),
        getAgents(),
      ]);
      setAreas(areasRes.data.areas);
      setPriorities(prioritiesRes.data.priorities);
      setStatuses(statusesRes.data.statuses);
      setAgents(agentsRes.data);
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await updateTask(id, {
        title: editForm.title,
        description: editForm.description,
        functional_area: editForm.functional_area,
        priority: editForm.priority,
        status: editForm.status,
        next_step: editForm.next_step,
        attachments: editForm.attachments || [],
        urls: editForm.urls || [],
      });
      setTask(response.data);
      setIsEditing(false);
      toast.success('Tarea actualizada');
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Error al actualizar la tarea');
    } finally {
      setSaving(false);
    }
  };

  const handleEditFileUpload = async (newFiles) => {
    setEditForm(prev => ({
      ...prev,
      attachments: [...(prev.attachments || []), ...newFiles]
    }));
  };

  const handleEditRemoveAttachment = (index) => {
    setEditForm(prev => ({
      ...prev,
      attachments: (prev.attachments || []).filter((_, i) => i !== index)
    }));
  };

  const handleEditAddUrl = () => {
    if (!editNewUrl.trim()) return;
    try {
      new URL(editNewUrl);
      setEditForm(prev => ({
        ...prev,
        urls: [...(prev.urls || []), editNewUrl.trim()]
      }));
      setEditNewUrl('');
    } catch {
      toast.error('URL no valida');
    }
  };

  const handleEditRemoveUrl = (index) => {
    setEditForm(prev => ({
      ...prev,
      urls: (prev.urls || []).filter((_, i) => i !== index)
    }));
  };

  const handleDelete = async () => {
    try {
      await deleteTask(id);
      toast.success('Tarea eliminada');
      navigate('/tasks');
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Error al eliminar la tarea');
    }
  };

  const handleAddStep = async () => {
    if (!newStep.description.trim()) {
      toast.error('La descripcion del paso es requerida');
      return;
    }

    try {
      const agent = agents.find(a => a.id === newStep.performed_by);
      await addTaskStep(id, {
        description: newStep.description,
        performed_by: newStep.performed_by || null,
        performed_by_name: agent?.name || null,
      });
      
      await loadTask();
      setShowStepDialog(false);
      setNewStep({ description: '', performed_by: '' });
      toast.success('Paso agregado');
    } catch (error) {
      console.error('Error adding step:', error);
      toast.error('Error al agregar el paso');
    }
  };

  const openEditStepDialog = (step) => {
    setEditingStep(step);
    setEditStepForm({
      description: step.description,
      performed_by: step.performed_by || ''
    });
  };

  const handleUpdateStep = async () => {
    if (!editStepForm.description.trim()) {
      toast.error('La descripcion del paso es requerida');
      return;
    }

    try {
      const agent = agents.find(a => a.id === editStepForm.performed_by);
      await updateTaskStep(id, editingStep.id, {
        description: editStepForm.description,
        performed_by: editStepForm.performed_by || null,
        performed_by_name: agent?.name || null,
      });
      
      await loadTask();
      setEditingStep(null);
      toast.success('Paso actualizado');
    } catch (error) {
      console.error('Error updating step:', error);
      toast.error('Error al actualizar el paso');
    }
  };

  const handleDeleteStep = async (stepId) => {
    if (!window.confirm('¿Estas seguro de eliminar este paso?')) {
      return;
    }

    try {
      await deleteTaskStep(id, stepId);
      await loadTask();
      toast.success('Paso eliminado');
    } catch (error) {
      console.error('Error deleting step:', error);
      toast.error('Error al eliminar el paso');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-10" data-testid="task-detail-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Link to="/tasks">
              <Button variant="ghost" size="icon" className="text-slate-600">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono text-sm text-slate-500">{task.sequential_number}</span>
                <Badge className={`${priorityColors[task.priority]} text-[10px] font-bold uppercase border`}>
                  {task.priority}
                </Badge>
                <Badge className={`${statusColors[task.status]} text-[10px] font-bold uppercase border`}>
                  {task.status}
                </Badge>
                {task.created_by_role === 'ugib' && (
                  <Badge className="bg-violet-100 text-violet-700 border border-violet-200 text-[10px] font-bold uppercase">
                    UGIB
                  </Badge>
                )}
              </div>
              <h1 className="text-2xl font-semibold text-slate-900 font-['Manrope'] tracking-tight">
                {task.title}
              </h1>
            </div>
          </div>
          
          {canManageTasks() && (
            <div className="flex gap-2 flex-wrap">
              {(task.internal_agents_details?.some(a => a.email) || task.external_agents_details?.some(a => a.email)) && (
                <Button
                  variant="outline"
                  onClick={openEmailModal}
                  className="border-blue-200 text-blue-700 hover:bg-blue-50"
                  data-testid="email-agents-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email Agentes
                </Button>
              )}
              <Button 
                variant="outline" 
                onClick={() => { setEditNewUrl(''); setIsEditing(true); }}
                className="border-stone-200"
                data-testid="edit-task-btn"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Editar
              </Button>
              {isSuperadmin() && (
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      const res = await toggleTaskVisibility(id);
                      setTask(prev => ({ ...prev, visible_to_departamentos: res.data.visible_to_departamentos }));
                      toast.success('Visibilidad actualizada');
                    } catch { toast.error('Error al cambiar visibilidad'); }
                  }}
                  className={task.visible_to_departamentos 
                    ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50" 
                    : "border-amber-200 text-amber-700 hover:bg-amber-50"}
                  data-testid="toggle-visibility-btn"
                  title={task.visible_to_departamentos ? 'Visible para Departamentos - clic para ocultar' : 'Oculta para Departamentos - clic para mostrar'}
                >
                  {task.visible_to_departamentos ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
                  {task.visible_to_departamentos ? 'Visible' : 'Oculta'}
                </Button>
              )}
              {isSuperadmin() && (
                <Button 
                  variant="outline" 
                  onClick={() => setShowDeleteDialog(true)}
                  className="border-red-200 text-red-600 hover:bg-red-50"
                  data-testid="delete-task-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <Card className="bg-white border border-stone-200 shadow-sm">
              <CardHeader className="border-b border-stone-100 pb-4">
                <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                  <FileText className="w-5 h-5 text-slate-600" />
                  Descripcion
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div 
                  className="text-slate-600 prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4"
                  dangerouslySetInnerHTML={{ __html: task.description || '<span class="text-slate-400">Sin descripcion</span>' }}
                />
              </CardContent>
            </Card>

            {/* Steps/History */}
            <Card className="bg-white border border-stone-200 shadow-sm">
              <CardHeader className="border-b border-stone-100 pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                    <Clock className="w-5 h-5 text-slate-600" />
                    Historial de Pasos
                  </CardTitle>
                  {canManageTasks() && (
                    <Button 
                      size="sm" 
                      onClick={() => setShowStepDialog(true)}
                      className="bg-slate-900 hover:bg-slate-800"
                      data-testid="add-step-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Agregar Paso
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {task.steps?.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    No hay pasos registrados
                  </div>
                ) : (
                  <div className="divide-y divide-stone-100">
                    {task.steps?.map((step, index) => (
                      <div key={step.id || index} className="p-4 group">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-sm font-medium text-slate-600">
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <div className="text-slate-900 prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4" dangerouslySetInnerHTML={{ __html: step.description }} />
                            <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                              {step.performed_by_name && (
                                <span className="flex items-center gap-1">
                                  <User className="w-4 h-4" />
                                  {step.performed_by_name}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {format(new Date(step.date), 'dd MMM yyyy HH:mm', { locale: es })}
                              </span>
                            </div>
                          </div>
                          {canManageTasks() && (
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openEditStepDialog(step)}
                                className="h-8 w-8 text-slate-400 hover:text-slate-600"
                                data-testid={`edit-step-${step.id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteStep(step.id)}
                                className="h-8 w-8 text-slate-400 hover:text-red-600"
                                data-testid={`delete-step-${step.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Next Step */}
            {task.next_step && (
              <Card className="bg-emerald-50 border border-emerald-200 shadow-sm">
                <CardContent className="p-4">
                  <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2">Proximo Paso</p>
                  <p className="text-emerald-900">{task.next_step}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Info */}
            <Card className="bg-white border border-stone-200 shadow-sm">
              <CardHeader className="border-b border-stone-100 pb-4">
                <CardTitle className="text-lg font-semibold text-slate-900">Informacion</CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Area Funcional</p>
                  <p className="text-slate-900 mt-1">{task.functional_area}</p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Creado por</p>
                  <p className="text-slate-900 mt-1">{task.created_by_name}</p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Fecha de creacion</p>
                  <p className="text-slate-900 mt-1">
                    {format(new Date(task.created_at), 'dd MMMM yyyy, HH:mm', { locale: es })}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Agents */}
            <Card className="bg-white border border-stone-200 shadow-sm">
              <CardHeader className="border-b border-stone-100 pb-4">
                <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                  <User className="w-5 h-5 text-slate-600" />
                  Agentes Asignados
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                {task.internal_agents_details?.length > 0 && (
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Internos</p>
                    <div className="space-y-2">
                      {task.internal_agents_details.map((agent) => (
                        <div key={agent.id} className="flex items-center gap-2 text-sm">
                          <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                            <User className="w-3 h-3 text-emerald-600" />
                          </div>
                          <span className="text-slate-900">{agent.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {task.external_agents_details?.length > 0 && (
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Externos</p>
                    <div className="space-y-2">
                      {task.external_agents_details.map((agent) => (
                        <div key={agent.id} className="flex items-center gap-2 text-sm">
                          <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                            <Building className="w-3 h-3 text-blue-600" />
                          </div>
                          <div>
                            <span className="text-slate-900">{agent.name}</span>
                            {agent.company && (
                              <span className="text-slate-500 text-xs ml-1">({agent.company})</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!task.internal_agents_details?.length && !task.external_agents_details?.length && (
                  <p className="text-sm text-slate-500">Sin agentes asignados</p>
                )}
              </CardContent>
            </Card>

            {/* Attachments */}
            {(task.attachments?.length > 0 || task.urls?.length > 0) && (
              <Card className="bg-white border border-stone-200 shadow-sm">
                <CardHeader className="border-b border-stone-100 pb-4">
                  <CardTitle className="text-lg font-semibold text-slate-900">Adjuntos</CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-3">
                  {task.attachments?.map((att, index) => {
                    const url = typeof att === 'string' ? att : att.url;
                    const filename = typeof att === 'string' ? `Archivo ${index + 1}` : att.filename;
                    return (
                      <a 
                        key={index}
                        href={url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                        data-testid={`attachment-${index}`}
                      >
                        <Image className="w-4 h-4" />
                        {filename}
                      </a>
                    );
                  })}
                  {task.urls?.map((url, index) => (
                    <a 
                      key={index}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-blue-600 hover:underline truncate"
                    >
                      <LinkIcon className="w-4 h-4 flex-shrink-0" />
                      {url}
                    </a>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="edit-task-dialog">
          <DialogHeader>
            <DialogTitle>Editar Tarea</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Titulo</Label>
              <Input
                value={editForm.title || ''}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Descripcion</Label>
              <RichTextEditor
                value={editForm.description || ''}
                onChange={(val) => setEditForm({ ...editForm, description: val })}
                placeholder="Descripcion de la tarea..."
                rows={4}
                data-testid="edit-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Area Funcional</Label>
                <Select 
                  value={editForm.functional_area} 
                  onValueChange={(v) => setEditForm({ ...editForm, functional_area: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {areas.map((area) => (
                      <SelectItem key={area} value={area}>{area}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Prioridad</Label>
                <Select 
                  value={editForm.priority} 
                  onValueChange={(v) => setEditForm({ ...editForm, priority: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priorities.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Estado</Label>
              <Select 
                value={editForm.status} 
                onValueChange={(v) => setEditForm({ ...editForm, status: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Proximo Paso</Label>
              <Textarea
                value={editForm.next_step || ''}
                onChange={(e) => setEditForm({ ...editForm, next_step: e.target.value })}
                rows={2}
                placeholder="Describe el siguiente paso a realizar..."
              />
            </div>

            {/* Attachments Section */}
            <div className="space-y-3 pt-2 border-t border-stone-100">
              <Label>Archivos Adjuntos</Label>
              <DropZone
                onFilesUploaded={handleEditFileUpload}
                uploading={editUploading}
                setUploading={setEditUploading}
              />
              {editForm.attachments?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {editForm.attachments.map((att, index) => {
                    const filename = typeof att === 'string' ? att.split('/').pop() : att.filename;
                    return (
                      <div 
                        key={index}
                        className="flex items-center gap-2 bg-stone-100 px-3 py-1 rounded-full text-sm"
                        data-testid={`edit-attachment-${index}`}
                      >
                        <Image className="w-4 h-4 text-slate-500" />
                        <span className="truncate max-w-[180px]">{filename}</span>
                        <button
                          type="button"
                          onClick={() => handleEditRemoveAttachment(index)}
                          className="text-slate-500 hover:text-red-500"
                          data-testid={`edit-remove-attachment-${index}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* URLs Section */}
            <div className="space-y-3">
              <Label>Enlaces</Label>
              <div className="flex gap-2">
                <Input
                  value={editNewUrl}
                  onChange={(e) => setEditNewUrl(e.target.value)}
                  placeholder="https://ejemplo.com"
                  className="border-stone-200"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleEditAddUrl())}
                  data-testid="edit-url-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleEditAddUrl}
                  className="border-stone-200"
                  data-testid="edit-add-url-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {editForm.urls?.length > 0 && (
                <div className="space-y-2">
                  {editForm.urls.map((url, index) => (
                    <div 
                      key={index}
                      className="flex items-center gap-2 bg-stone-100 px-3 py-2 rounded-lg text-sm"
                      data-testid={`edit-url-${index}`}
                    >
                      <LinkIcon className="w-4 h-4 text-slate-500 flex-shrink-0" />
                      <span className="truncate flex-1">{url}</span>
                      <button
                        type="button"
                        onClick={() => handleEditRemoveUrl(index)}
                        className="text-slate-500 hover:text-red-500"
                        data-testid={`edit-remove-url-${index}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditing(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-slate-900 hover:bg-slate-800">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Step Dialog */}
      <Dialog open={showStepDialog} onOpenChange={setShowStepDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="add-step-dialog">
          <DialogHeader>
            <DialogTitle>Agregar Paso</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Descripcion del paso</Label>
              <RichTextEditor
                value={newStep.description}
                onChange={(val) => setNewStep({ ...newStep, description: val })}
                placeholder="Describe lo que se realizo..."
                rows={3}
                data-testid="step-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Realizado por (opcional)</Label>
              <Select 
                value={newStep.performed_by} 
                onValueChange={(v) => setNewStep({ ...newStep, performed_by: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar agente..." />
                </SelectTrigger>
                <SelectContent>
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name} {agent.company && `(${agent.company})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStepDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddStep} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" />
              Agregar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Step Dialog */}
      <Dialog open={!!editingStep} onOpenChange={() => setEditingStep(null)}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="edit-step-dialog">
          <DialogHeader>
            <DialogTitle>Editar Paso</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Descripcion del paso</Label>
              <RichTextEditor
                value={editStepForm.description}
                onChange={(val) => setEditStepForm({ ...editStepForm, description: val })}
                placeholder="Describe lo que se realizo..."
                rows={3}
                data-testid="edit-step-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Realizado por (opcional)</Label>
              <Select 
                value={editStepForm.performed_by} 
                onValueChange={(v) => setEditStepForm({ ...editStepForm, performed_by: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar agente..." />
                </SelectTrigger>
                <SelectContent>
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name} {agent.company && `(${agent.company})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingStep(null)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateStep} className="bg-slate-900 hover:bg-slate-800">
              <Save className="w-4 h-4 mr-2" />
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Tarea</AlertDialogTitle>
            <AlertDialogDescription>
              Esta accion no se puede deshacer. Se eliminara permanentemente la tarea 
              <strong> {task.sequential_number}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Agents Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Email a Agentes
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Asunto</Label>
              <p className="text-sm text-slate-800 mt-1 bg-stone-50 p-2 rounded border border-stone-200">
                Tarea {task?.sequential_number} - {task?.title}
              </p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                Destinatarios
              </Label>
              <div className="space-y-2">
                {emailRecipients.length === 0 ? (
                  <p className="text-sm text-slate-500">No hay agentes con email asignados</p>
                ) : (
                  emailRecipients.map((r, i) => (
                    <label
                      key={r.id}
                      className="flex items-center gap-3 p-2 rounded border border-stone-200 hover:bg-stone-50 cursor-pointer"
                      data-testid={`email-recipient-${i}`}
                    >
                      <input
                        type="checkbox"
                        checked={r.selected}
                        onChange={() => {
                          setEmailRecipients(prev =>
                            prev.map((rec, idx) => idx === i ? { ...rec, selected: !rec.selected } : rec)
                          );
                        }}
                        className="rounded border-stone-300"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{r.name}</p>
                        <p className="text-xs text-slate-500 truncate">{r.email}</p>
                      </div>
                      <X
                        className="w-4 h-4 text-slate-400 hover:text-red-500 cursor-pointer shrink-0"
                        onClick={(e) => {
                          e.preventDefault();
                          setEmailRecipients(prev => prev.filter((_, idx) => idx !== i));
                        }}
                      />
                    </label>
                  ))
                )}
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                Mensaje (opcional)
              </Label>
              <Textarea
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                placeholder="Escribe el cuerpo del email..."
                rows={5}
                className="resize-y"
                data-testid="email-body-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={sendEmail}
              disabled={!emailRecipients.some(r => r.selected)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="send-email-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Abrir Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default TaskDetailPage;
